import { api } from "@/api";
import { OfficeLocation } from "@/api/locations/locations";
import { MeetingRoom, MeetingRoomRent } from "@/api/meetingRooms";
import { MeetingRoomDateControls } from "@/app/components/meetingRooms/meetingRoomDateControls";
import {
  MeetingRoomTimeline,
  TimelineEvent,
  TimelineRoom,
} from "@/app/components/meetingRooms/meetingRoomTimeline";
import { CreateMeetingRoomRentModal } from "@/app/components/modals/meetingRooms/createMeetingRoomRent";
import { EditMeetingRoomRentModal } from "@/app/components/modals/meetingRooms/editMeetingRoomRent";
import { setTour } from "@/app/store/user/reducer";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import useErrorHandling from "@/hooks/useErrorHandling";
import {
  wallDateFromParts,
  wallDateTimeFromIso,
} from "@/utils/meetingRoomWallTime";
import { Tab, Tabs, useDisclosure } from "@heroui/react";
import { addDays, format } from "date-fns";
import { ru } from "date-fns/locale";
import { Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

export function MeetingRoomsPage() {
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const [meetingRooms, setMeetingRooms] = useState<MeetingRoom[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [meetingRoomsLoading, setMeetingRoomsLoading] = useState<boolean>(true);
  const [meetingRoomsError, setMeetingRoomsError] = useState<string | null>(
    null,
  );

  const [roomBookings, setRoomBookings] = useState<
    Record<number, MeetingRoomRent[]>
  >({});
  const [roomAvailability, setRoomAvailability] = useState<
    Record<number, { rent_from: string; rent_to: string }[]>
  >({});
  const [bookingsErrors, setBookingsErrors] = useState<
    Record<number, string | null>
  >({});
  const [bookingsLoading, setBookingsLoading] = useState<
    Record<number, boolean>
  >({});

  const [time, setTime] = useState<number | null>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const [activeMeetingRoom, setActiveMeetingRoom] =
    useState<MeetingRoom | null>(null);
  const [activeRent, setActiveRent] = useState<MeetingRoomRent | null>(null);
  const [draftRentRange, setDraftRentRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const createRentDisclosure = useDisclosure();
  const editRentDisclosure = useDisclosure();

  const dispatch = useAppDispatch();
  const { tour } = useAppSelector((state) => state.user);
  const users = useAppSelector((state) => state.handbooks.user);

  const handleError = useErrorHandling();

  const dayStart = useMemo(() => {
    const y = currentDate.getFullYear();
    const mo = currentDate.getMonth() + 1;
    const d = currentDate.getDate();
    return wallDateFromParts(y, mo, d, 0, 0, 0);
  }, [currentDate]);

  const dayEnd = useMemo(() => {
    const anchor = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
    );
    const next = addDays(anchor, 1);
    return wallDateFromParts(
      next.getFullYear(),
      next.getMonth() + 1,
      next.getDate(),
      0,
      0,
      0,
    );
  }, [currentDate]);
  const formattedCurrentDate = useMemo(
    () => format(currentDate, "d MMMM yyyy", { locale: ru }),
    [currentDate],
  );

  const orientation = isMobile ? "vertical" : "horizontal";

  useEffect(() => {
    if (tour.isTourActive && tour.stepIndex == 0) {
      dispatch(setTour({ run: true, stepIndex: 3 }));
    }
  }, [tour, dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const loadLocationsAndRooms = useCallback(async () => {
    setMeetingRoomsLoading(true);
    setMeetingRoomsError(null);

    try {
      const locationsRes = await api.locations.list();
      setLocations(locationsRes.data);

      const rooms: MeetingRoom[] = [];
      const locationRequests = locationsRes.data.map((loc) =>
        api.meetingRooms.list(loc.id).then((res) => {
          res.data.forEach((room) => rooms.push(room));
        }),
      );

      await Promise.all(locationRequests);
      setMeetingRooms(rooms);

      // Set initial selected location
      if (rooms.length > 0 && !selectedLocationId) {
        const firstLocationId = rooms[0].location_id;
        setSelectedLocationId(firstLocationId);
      }
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { errorMessage } = handleError(err as any);
      setMeetingRoomsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setMeetingRoomsLoading(false);
    }
  }, [handleError, selectedLocationId]);

  const loadRemainingTime = useCallback(async () => {
    try {
      const res = await api.meetingRooms.getRemainingTime();
      setTime(res.data.time);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { errorMessage } = handleError(err as any);
      toast.error(errorMessage);
    }
  }, [handleError]);

  useEffect(() => {
    loadLocationsAndRooms();
    loadRemainingTime();
  }, [loadLocationsAndRooms, loadRemainingTime]);

  const roomsBySelectedLocation = useMemo(() => {
    if (!selectedLocationId) return [];
    return meetingRooms.filter(
      (room) => room.location_id === selectedLocationId,
    );
  }, [meetingRooms, selectedLocationId]);

  const fetchBookingsForRooms = useCallback(
    async (roomsToLoad: MeetingRoom[]) => {
      if (roomsToLoad.length === 0) return;

      const dateStr = format(currentDate, "yyyy-MM-dd");

      setBookingsLoading((prev) => {
        const next = { ...prev };
        roomsToLoad.forEach((room) => {
          next[room.id] = true;
        });
        return next;
      });

      const results = await Promise.all(
        roomsToLoad.map(async (room) => {
          try {
            // Fetch company bookings for this room
            const bookingsRes = await api.meetingRooms.getRoomBookings(
              room.id,
              dateStr,
            );

            // Filter bookings to ensure they belong to this room
            // This prevents duplicate bookings if the API returns all company bookings
            const bookings = bookingsRes.data.filter(
              (b) => b.meeting_room_id === room.id,
            );

            // Fetch availability (occupied by other companies)
            const availabilityRes = await api.meetingRooms.getAvailability(
              dateStr + "T00:01:02.000Z",
              room.id,
            );

            return {
              roomId: room.id,
              bookings: bookings,
              availability: availabilityRes.data,
              error: null,
            };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { errorMessage } = handleError(error as any);
            return {
              roomId: room.id,
              bookings: [] as MeetingRoomRent[],
              availability: [] as { rent_from: string; rent_to: string }[],
              error: errorMessage,
            };
          }
        }),
      );

      setRoomBookings((prev) => {
        const next = { ...prev };
        results.forEach(({ roomId, bookings }) => {
          next[roomId] = bookings;
        });
        return next;
      });

      setRoomAvailability((prev) => {
        const next = { ...prev };
        results.forEach(({ roomId, availability }) => {
          next[roomId] = availability;
        });
        return next;
      });

      setBookingsErrors((prev) => {
        const next = { ...prev };
        results.forEach(({ roomId, error }) => {
          next[roomId] = error;
        });
        return next;
      });

      setBookingsLoading((prev) => {
        const next = { ...prev };
        roomsToLoad.forEach((room) => {
          next[room.id] = false;
        });
        return next;
      });
    },
    [currentDate, handleError],
  );

  const refreshSelectedLocationBookings = useCallback(() => {
    if (!selectedLocationId) return;
    const roomsToRefresh = meetingRooms.filter(
      (room) => room.location_id === selectedLocationId,
    );
    fetchBookingsForRooms(roomsToRefresh);
  }, [fetchBookingsForRooms, meetingRooms, selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) return;
    fetchBookingsForRooms(roomsBySelectedLocation);
  }, [
    selectedLocationId,
    roomsBySelectedLocation,
    fetchBookingsForRooms,
    currentDate,
  ]);

  useEffect(() => {
    setActiveMeetingRoom(null);
    setActiveRent(null);
    setDraftRentRange(null);
  }, [selectedLocationId]);

  const locationOptions = useMemo(
    () =>
      Array.from(new Set(meetingRooms.map((room) => room.location_id)))
        .map((id) => ({
          id,
          label:
            locations.find((loc) => loc.id === id)?.display_name ||
            `Локация ${id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "ru")),
    [meetingRooms, locations],
  );

  const timelineRooms = useMemo<TimelineRoom[]>(() => {
    return roomsBySelectedLocation.map((room) => {
      const bookings = roomBookings[room.id] || [];
      const availability = roomAvailability[room.id] || [];

      const events: TimelineEvent[] = [];

      // Add company bookings (blue)
      bookings.forEach((rent) => {
        const rentStart = wallDateTimeFromIso(rent.rent_from);
        const rentEnd = wallDateTimeFromIso(rent.rent_to);

        if (rentEnd <= dayStart || rentStart >= dayEnd) {
          return;
        }

        const user = users.find((usr) => usr.id === rent.user_id);
        const userName = [user?.first_name, user?.last_name]
          .filter(Boolean)
          .join(" ");

        const clippedStart =
          rentStart < dayStart ? new Date(dayStart) : rentStart;
        const clippedEnd = rentEnd > dayEnd ? new Date(dayEnd) : rentEnd;

        events.push({
          id: rent.id,
          start: clippedStart,
          end: clippedEnd,
          label: userName || `Бронь #${rent.id}`,
          rent,
          isCompanyEvent: true,
        });
      });

      // Add availability (gray) - occupied by other companies
      availability.forEach((slot, index) => {
        const slotStart = wallDateTimeFromIso(slot.rent_from);
        const slotEnd = wallDateTimeFromIso(slot.rent_to);

        if (slotEnd <= dayStart || slotStart >= dayEnd) {
          return;
        }

        // Check for overlap with any company booking
        const hasOverlap = bookings.some((booking) => {
          const bookingStart = wallDateTimeFromIso(booking.rent_from);
          const bookingEnd = wallDateTimeFromIso(booking.rent_to);

          return slotStart < bookingEnd && slotEnd > bookingStart;
        });

        if (hasOverlap) return;

        const clippedStart =
          slotStart < dayStart ? new Date(dayStart) : slotStart;
        const clippedEnd = slotEnd > dayEnd ? new Date(dayEnd) : slotEnd;

        events.push({
          id: -1000 - index, // Negative IDs for availability
          start: clippedStart,
          end: clippedEnd,
          label: "Занято",
          isCompanyEvent: false,
        });
      });

      return {
        room,
        events,
        isLoading: bookingsLoading[room.id],
        error: bookingsErrors[room.id],
      };
    });
  }, [
    roomsBySelectedLocation,
    roomBookings,
    roomAvailability,
    bookingsErrors,
    bookingsLoading,
    users,
    dayStart,
    dayEnd,
  ]);

  const handleTimelineEventClick = useCallback(
    (room: MeetingRoom, event: TimelineEvent) => {
      // Only allow editing company events (blue ones)
      if (!event.isCompanyEvent || !event.rent) return;

      setActiveMeetingRoom(room);
      setActiveRent(event.rent);
      editRentDisclosure.onOpen();
    },
    [editRentDisclosure],
  );

  const handleTimelineEmptyClick = useCallback(
    (room: MeetingRoom, range: { start: Date; end: Date }) => {
      setActiveMeetingRoom(room);
      setActiveRent(null);
      setDraftRentRange(range);
      createRentDisclosure.onOpen();
    },
    [createRentDisclosure],
  );

  const handleLocationTabChange = useCallback((key: Key) => {
    if (key == null) return;
    const nextId = Number(key);
    setSelectedLocationId(Number.isNaN(nextId) ? null : nextId);
  }, []);

  const prevCreateRentOpen = useRef(false);
  useEffect(() => {
    if (prevCreateRentOpen.current && !createRentDisclosure.isOpen) {
      setDraftRentRange(null);
      setActiveMeetingRoom(null);
      setActiveRent(null);
      refreshSelectedLocationBookings();
    }
    prevCreateRentOpen.current = createRentDisclosure.isOpen;
  }, [createRentDisclosure.isOpen, refreshSelectedLocationBookings]);

  const prevEditRentOpen = useRef(false);
  useEffect(() => {
    if (prevEditRentOpen.current && !editRentDisclosure.isOpen) {
      setActiveMeetingRoom(null);
      setActiveRent(null);
      refreshSelectedLocationBookings();
    }
    prevEditRentOpen.current = editRentDisclosure.isOpen;
  }, [editRentDisclosure.isOpen, refreshSelectedLocationBookings]);

  const activeLocationKey = selectedLocationId
    ? selectedLocationId.toString()
    : null;

  const isTimelineLoading =
    meetingRoomsLoading ||
    (timelineRooms.length > 0 && timelineRooms.every((room) => room.isLoading));

  function formatDateTimeLocal(value: Date) {
    const pad = (num: number) => num.toString().padStart(2, "0");
    const year = value.getUTCFullYear();
    const month = pad(value.getUTCMonth() + 1);
    const day = pad(value.getUTCDate());
    const hours = pad(value.getUTCHours());
    const minutes = pad(value.getUTCMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  return (
    <>
      <CreateMeetingRoomRentModal
        disclosure={createRentDisclosure}
        meetingRoom={activeMeetingRoom}
        initialDate={draftRentRange ? draftRentRange.start : currentDate}
        defaultValues={
          draftRentRange
            ? {
                rent_from: formatDateTimeLocal(draftRentRange.start),
                rent_to: formatDateTimeLocal(draftRentRange.end),
              }
            : undefined
        }
      />
      <EditMeetingRoomRentModal
        disclosure={editRentDisclosure}
        rent={activeRent}
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow flex flex-col gap-2">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                  <div className="flex flex-col">
                    <span className="text-xl font-semibold">Переговорки</span>
                    <span className="text-sm text-foreground-500">
                      Таймлайн бронирований на {formattedCurrentDate}
                    </span>
                  </div>
                </div>
              </div>

              <span className="p-4 border-2 border-primary-500 rounded-xl max-w-fit">
                В этом месяце вы можете потратить на аренду ещё {time} часов
              </span>

              {activeLocationKey && locationOptions.length > 0 ? (
                <Tabs
                  aria-label="Локации переговорок"
                  selectedKey={activeLocationKey}
                  onSelectionChange={handleLocationTabChange}
                  color="primary"
                  variant="bordered"
                  className="w-full"
                  classNames={{ tabList: "gap-2 flex-wrap md:flex-nowrap" }}>
                  {locationOptions.map((option) => (
                    <Tab key={option.id.toString()} title={option.label} />
                  ))}
                </Tabs>
              ) : null}

              <MeetingRoomDateControls
                date={currentDate}
                onChange={setCurrentDate}
                className="flex flex-wrap items-center gap-2"
              />

              {meetingRoomsError ? (
                <div className="rounded-xl border border-danger-300 bg-danger-100/80 p-4 text-sm text-danger-700 dark:border-danger-500 dark:bg-danger-500/20 dark:text-danger-200">
                  {meetingRoomsError}
                </div>
              ) : null}
            </div>

            <div
              className="flex flex-col gap-2 w-full"
              id="meeting-rooms-title">
              {meetingRoomsLoading && meetingRooms.length === 0 ? (
                <div className="flex flex-col gap-3">
                  {[0, 1, 2].map((idx) => (
                    <div
                      key={idx}
                      className="h-32 w-full rounded-2xl skeleton-shimmer"
                    />
                  ))}
                </div>
              ) : null}

              {!meetingRoomsLoading && !selectedLocationId ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/10 bg-content2/40 p-8 text-center text-sm text-foreground-500">
                  <span>У вас пока нет локаций с переговорами.</span>
                  <span>
                    Добавьте первую переговорку, чтобы увидеть таймлайн.
                  </span>
                </div>
              ) : null}

              {!meetingRoomsLoading &&
              selectedLocationId &&
              roomsBySelectedLocation.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/10 bg-content2/40 p-8 text-center text-sm text-foreground-500">
                  <span>В выбранной локации пока нет переговорок.</span>
                  <span>
                    Создайте новую переговорку или выберите другую локацию.
                  </span>
                </div>
              ) : null}

              {selectedLocationId &&
              roomsBySelectedLocation.length > 0 &&
              !meetingRoomsLoading ? (
                <div className="flex flex-col gap-6">
                  <MeetingRoomTimeline
                    date={currentDate}
                    rooms={timelineRooms}
                    orientation={orientation}
                    intervalMinutes={30}
                    startHour={8}
                    endHour={22}
                    onEventClick={handleTimelineEventClick}
                    onEmptySlotClick={handleTimelineEmptyClick}
                  />
                </div>
              ) : null}

              {!meetingRoomsLoading &&
                !isTimelineLoading &&
                timelineRooms.some((room) => room.error) && (
                  <div className="flex flex-col gap-2 rounded-xl border border-warning-300 bg-warning-100/70 p-4 text-sm text-warning-700 dark:border-warning-500 dark:bg-warning-500/20 dark:text-warning-200">
                    <span>
                      Не удалось загрузить брони для некоторых переговорок.
                      Попробуйте обновить страницу позднее.
                    </span>
                    <ul className="list-disc pl-6">
                      {timelineRooms
                        .filter((room) => room.error)
                        .map((room) => (
                          <li key={room.room.id}>{room.room.display_name}</li>
                        ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
